import { WarningIcon } from "@/components/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "cn";

import { USAGE_TEXTS as TEXTS } from "./texts.ts";
import {
  describeCost,
  formatTokens,
  turnTotals,
  type TurnUsage,
} from "./usage.ts";
import { UsageBreakdown } from "./UsageBreakdown.tsx";

/**
 * Lo que costó un turno, al pie de la respuesta (2.4.1).
 *
 * Una línea discreta: está para quien la busca, no para competir con la
 * respuesta. El desglose entero va en el tooltip.
 *
 * El aviso de turno caro (parte 3, punto 5) va en la MISMA línea y no en una
 * alerta aparte: lo que dice es "este número es alto", así que tiene que estar
 * pegado al número. Y nombra la herramienta que más pesó, que es lo único que
 * permite hacer algo al respecto.
 */
export function TurnUsageFooter({ usage }: { usage: TurnUsage }) {
  const totals = turnTotals(usage);
  const cost = describeCost(totals.costUsd, totals.unpricedCalls);
  const cached = totals.cachedInput + totals.cacheWriteInput;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="cursor-default tabular-nums hover:text-foreground"
          >
            {TEXTS.turn.input(formatTokens(totals.input))}
            {cached > 0 && TEXTS.turn.cache(formatTokens(cached))}
            {" · "}
            {TEXTS.turn.output(formatTokens(totals.output))}
            {" · "}
            <span className={cn(cost.kind !== "estimated" && "text-warning")}>
              {cost.medium}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="start">
          <UsageBreakdown
            usage={totals}
            title={
              usage.model === null
                ? TEXTS.turn.title
                : TEXTS.turn.titleWithModel(usage.model)
            }
          />
        </TooltipContent>
      </Tooltip>

      {usage.warning !== undefined && (
        <span
          className="inline-flex items-center gap-1 text-warning"
          role="note"
        >
          <WarningIcon className="size-3.5" aria-hidden />
          {TEXTS.turn.expensive(formatTokens(usage.warning.threshold))}
          {usage.warning.topTool !== null && (
            <>
              {TEXTS.turn.heaviest}
              <code>{usage.warning.topTool}</code>
            </>
          )}
        </span>
      )}
    </div>
  );
}
